import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { processVitalSigns } from "../utils/vitalSignsProcessor";
import companyLogo from "../assets/zakhealth-logo.svg";

// Import icons from lucide-react
import {
    Heart,
    Activity,
    Smile,
    Shield,
    TrendingUp,
    GitPullRequest,
    Zap,
    Watch,
    Wind,
    BarChart2,
    DivideCircle,
    AlertTriangle, // --- MODIFIED: Added icon for error state
} from "lucide-react";
import VitalSignCard from "./VitalSignCard";
import WellnessGauge from "components/WellnessGauge";
import Tabs from "components/Tabs";
import { getClientHomePath } from "utils/client";

// --- TYPE DEFINITIONS ---
type Summaries = ReturnType<typeof processVitalSigns> | null;
// --- MODIFIED: Added a type for our component's status
type PageStatus = "loading" | "success" | "error";

// --- STYLED COMPONENTS ---
const PageWrapper = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
`;

const AppHeader = styled.header`
  background-color: #ffffff;
  padding: 16px 40px;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
  box-sizing: border-box;
`;

const LogoImage = styled.img`
  height: 45px;
  display: block;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

// --- MODIFIED: Added styled components for the error/loading states ---
const StateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  height: calc(100vh - 150px); // Adjust height to fill space below header
`;

const StateMessage = styled.p`
  font-size: 1.1rem;
  color: #4b5563;
  margin-top: 16px;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.6;
`;

const SectionHeader = styled.h2`
  margin-top: 24px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 1.5rem;
  font-weight: 700;
  color: #374151;
`;

const Grid = styled.div<{ singleItem?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  ${(props) =>
        props.singleItem &&
        `
    grid-template-columns: 1fr;
    max-width: 450px;
    margin: 0 auto;
  `}
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const TabContent = styled.div`
  animation: ${fadeIn} 0.5s ease;
`;

const DoneButton = styled.button`
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 60px auto 20px;
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  background-color: #bc1414ff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
  box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #bc1414ff;
    transform: translateY(-2px);
  }
`;

// --- COMPONENT LOGIC ---
const TABS_CONFIG = [
    { id: "vitals", label: "Vital Signs" },
    { id: "stress", label: "Stress & Wellness" },
    { id: "hrv", label: "Advanced HRV" },
];

const ResultsPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [summaries, setSummaries] = useState<Summaries>(null);
    const [activeTab, setActiveTab] = useState(TABS_CONFIG[0].id);
    const [status, setStatus] = useState<PageStatus>("loading");
    const rawData = location.state?.data;

    const clientDetails = localStorage.getItem("clientDetails");
    const clientData = clientDetails ? JSON.parse(clientDetails) : null;
    const clientLogo = clientData?.brand_image;
    const clientName = clientData?.client_name;

    useEffect(() => {
        if (rawData) {
            const processedData = processVitalSigns(rawData);
            let totalResultCount = rawData ? Object.keys(rawData).length : 0;
            if (processedData && processedData.wellnessIndex && totalResultCount >= 5) {
                setSummaries(processedData);
                setStatus("success");
            } else {
                console.log("Failed to process vital signs from the provided data.");
                setStatus("error");
            }
        } else {
            console.log("No results data found in location state.");
            setStatus("error");
        }
    }, [rawData, navigate]);

    const renderTabContent = () => {
        if (!summaries) return null;

        switch (activeTab) {
            case "vitals": {
                const vitalsCards = [
                    clientData?.show_blood_pressure === 1 && summaries.bloodPressure && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Blood Pressure"
                            summary={summaries.bloodPressure}
                            icon={<Activity size={22} />}
                        />
                    ),
                    clientData?.show_pulse_rate === 1 && summaries.heartRate && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Heart Rate"
                            confidenceLevel={summaries.heartRate?.confidenceLevel || null}
                            summary={summaries.heartRate}
                            icon={<Heart size={22} />}
                        />
                    ),
                    clientData?.show_respiration_rate === 1 &&
                    summaries.respirationRate && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Respiration Rate"
                            confidenceLevel={
                                summaries.respirationRate?.confidenceLevel || null
                            }
                            summary={summaries.respirationRate}
                            icon={<Wind size={22} />}
                        />
                    ),
                    clientData?.show_prq === 1 && summaries.prq && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Pulse-Respiration Quotient"
                            confidenceLevel={summaries.prq?.confidenceLevel || null}
                            summary={summaries.prq}
                            icon={<DivideCircle size={22} />}
                        />
                    ),
                ].filter(Boolean);

                return vitalsCards.length > 0 ? (
                    <>
                        <SectionHeader>Primary Vital Signs</SectionHeader>
                        <Grid>{vitalsCards}</Grid>
                    </>
                ) : null;
            }

            case "stress": {
                const stressCards = [
                    clientData?.show_stress_level === 1 && summaries.stressLevel && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Stress Level (1-5)"
                            summary={summaries.stressLevel}
                            icon={<Shield size={22} />}
                        />
                    ),
                    clientData?.show_stress_index === 1 && summaries.stressIndex && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Baevsky's Stress Index"
                            summary={summaries.stressIndex}
                            icon={<TrendingUp size={22} />}
                        />
                    ),
                ].filter(Boolean);

                const wellnessCards = [
                    clientData?.show_wellness_score === 1 && summaries.wellnessIndex && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Wellness Index"
                            summary={summaries.wellnessIndex}
                            icon={<Zap size={22} />}
                        />
                    ),
                ].filter(Boolean);

                return (
                    <>
                        {stressCards.length > 0 && (
                            <>
                                <SectionHeader>Stress Analysis</SectionHeader>
                                <Grid>{stressCards}</Grid>
                            </>
                        )}
                        {wellnessCards.length > 0 && (
                            <>
                                <SectionHeader>Wellness Details</SectionHeader>
                                <Grid>{wellnessCards}</Grid>
                            </>
                        )}
                    </>
                );
            }

            case "hrv": {
                const hrvCards = [
                    clientData?.show_mean_rri === 1 && summaries.meanRri && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Mean R-R Interval"
                            confidenceLevel={summaries.meanRri?.confidenceLevel || null}
                            summary={summaries.meanRri}
                            icon={<Watch size={22} />}
                        />
                    ),
                    clientData?.show_hrv_sdnn === 1 && summaries.hrvSdnn && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="HRV (SDNN)"
                            confidenceLevel={summaries.hrvSdnn?.confidenceLevel || null}
                            summary={summaries.hrvSdnn}
                            icon={<Activity size={22} />}
                        />
                    ),
                    clientData?.show_hrv_rmssd === 1 && summaries.hrvRmssd && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="HRV (RMSSD)"
                            summary={summaries.hrvRmssd}
                            icon={<Activity size={22} />}
                        />
                    ),
                    clientData?.show_poincare === 1 && summaries.poincare && (
                        <VitalSignCard
                            key={Math.random().toString()}
                            title="Poincaré Plot (SD1/SD2)"
                            summary={summaries.poincare}
                            icon={<GitPullRequest size={22} />}
                        />
                    ),
                ].filter(Boolean);

                return hrvCards.length > 0 ? (
                    <>
                        <SectionHeader>Heart Rate Variability Details</SectionHeader>
                        <Grid>{hrvCards}</Grid>
                    </>
                ) : null; // 🔥 hides the whole HRV section if no data
            }

            default:
                return null;
        }
    };

    // --- MODIFIED: Conditional rendering based on status
    if (status === "loading") {
        return (
            <PageWrapper>
                <AppHeader>
                    {clientLogo ? (
                        <LogoImage src={clientLogo} alt="Client Logo" />
                    ) : clientName ? (
                        <span className="text-lg font-semibold text-gray-800">
                            {clientName}
                        </span>
                    ) : (
                        <LogoImage src={companyLogo} alt="Zak Health Logo" />
                    )}
                </AppHeader>
                <StateWrapper>
                    <StateMessage>Analyzing Your Vitals...</StateMessage>
                </StateWrapper>
            </PageWrapper>
        );
    }

    if (status === "error") {
        return (
            <PageWrapper>
                <AppHeader>
                    {clientLogo ? (
                        <LogoImage src={clientLogo} alt="Client Logo" />
                    ) : clientName ? (
                        <span className="text-lg font-semibold text-gray-800">
                            {clientName}
                        </span>
                    ) : (
                        <LogoImage src={companyLogo} alt="Zak Health Logo" />
                    )}
                </AppHeader>
                <StateWrapper>
                    <AlertTriangle size={48} color="#EF4444" />
                    <SectionHeader>Data Unavailable</SectionHeader>
                    <StateMessage>
                        We couldn't get the necessary vital signs. Please try again,
                        ensuring your face is well-lit and you remain still during the scan.
                    </StateMessage>
                    {/* Re-using DoneButton style for the "Scan Again" action */}
                    <DoneButton
                        onClick={() => {
                            // Client-side navigation reuses the SDK's already-loaded wasm;
                            // a full page load would allocate another 512 MB (error 7014).
                            navigate("/scan");
                        }}
                    >
                        Scan Again
                    </DoneButton>
                </StateWrapper>
            </PageWrapper>
        );
    }

    // This part will only render if status is 'success' and summaries is available.
    return (
        <PageWrapper>
            <AppHeader>
                {clientLogo ? (
                    <LogoImage src={clientLogo} alt="Client Logo" />
                ) : clientName ? (
                    <span className="text-lg font-semibold text-gray-800">
                        {clientName}
                    </span>
                ) : (
                    <LogoImage src={companyLogo} alt="Zak Health Logo" />
                )}
            </AppHeader>

            {/* data-private: LogRocket doesn't record the vital-sign results */}
            <Container data-private>
                {summaries && (
                    <>
                        {clientData && clientData.show_wellness_score == 1 && (
                            <WellnessGauge
                                score={rawData.wellnessIndex.value}
                                category={summaries.wellnessIndex?.category || ""}
                            />
                        )}

                        <Tabs
                            tabs={TABS_CONFIG}
                            activeTab={activeTab}
                            onTabClick={setActiveTab}
                        />

                        <TabContent>{renderTabContent()}</TabContent>

                        {/* Client-side navigation keeps the loaded SDK for the next scan; a full
                            page load allocates its 512 MB of memory again (error 7014). */}
                        <DoneButton onClick={() => navigate(getClientHomePath(clientData))}>
                            Done
                        </DoneButton>
                    </>
                )}
            </Container>
            {/* Powered by Zak Health with logo */}
            <div className="sticky bottom-0 mt-8 py-3 flex items-center justify-center gap-3 bg-white border-t">
                <p className="text-gray-500 text-sm">Powered by</p>
                <div className="flex items-center">
                    <img
                        src={companyLogo}
                        alt="Zak Health Logo"
                        className="h-6 w-auto object-contain"
                    />
                </div>
            </div>
        </PageWrapper>
    );
};

export default ResultsPage;
